import { useEffect, useRef } from 'react';
import * as proveAuth from '@prove-identity/prove-auth';
import PhoneNumberInput from '@prove-identity/prove-auth/build/lib/proveauth/internal/phone-number-input';
import Footer from '@components/shared/Footer/Footer';
import { useTexts } from 'utils/useTexts';
import { useFormContext } from '@context/FormContext';
import { Button } from '@components/shared/Button/Button';
import { Screens } from '@shared/constants/screens';
import { createErrorResponse } from '@services/utils';
import { AssetLinks } from '@assets/assets';
import { useProve } from '@context/proveProvider';
import { validate } from '@services/prove';
import { CDX_ACTIONS } from 'types/form';
import ProveClientSdk from '@services/proveClientSdk';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import useEffectOnce from '@shared/hooks/useEffectOnce';
import { SEGMENT_SCREEN_PARAMS } from '@shared/constants/segmentConstants';
import { useScreenNavigator } from '@shared/hooks/useScreenNavigator';

const VerifyMobileNumberCheckYourPhone = () => {
  const { t } = useTexts();
  const { next } = useScreenNavigator();
  const { trackPage, trackClick } = useAnalytics(SEGMENT_SCREEN_PARAMS.PROVE_IDENTITY);
  const { currentStep, verifyYourIdentity, handleStep, dispatch } = useFormContext();
  const { authToken, correlationId, setChallengeMissing, setNextStep, setAuthToken } = useProve();
  const authRef = useRef<proveAuth.CancelablePromise<void> | null>(null);
  const cancelledRef = useRef<boolean>(false);

  useEffect(() => {
    next(currentStep, 'onLoad', { authToken });
    cancelledRef.current = false;
    sendInstantLink(authToken as string);

    return () => {
      setAuthToken('');
      if (authRef.current) {
        authRef.current.cancel();
      }
    };
  }, [authToken]);

  const getformattedPhoneNumber = () => {
    return verifyYourIdentity.phone.slice(-4);
  };

  useEffectOnce(() =>
    trackPage(t('pageTitles.VerifyYourMobileNumber'), {
      sub_screen_name: 'verify_mobile_wait',
      verification_method: 'sms_code',
      challenge_type: 'carrier_link',
      masked_phone: getformattedPhoneNumber(),
      resend_available: true,
      page_type: 'landing_page',
    })
  );

  const sendInstantLink = async (authToken: string) => {
    try {
      const proveClientSdk = new ProveClientSdk();
      const builder = proveClientSdk.handleInstantLink(instantLinkStartStep, authFinishStep);
      authRef.current = builder.authenticate(authToken);
      await authRef.current;
    } catch (error) {
      dispatch({
        type: CDX_ACTIONS.SUBMIT_API_ERROR,
        payload: createErrorResponse(
          error instanceof Error ? error.name : 400,
          error instanceof Error ? error.message : 'Error sending instant link.'
        ),
      });
    }
  };

  const instantLinkStartStep: proveAuth.InstantLinkStartStep = {
    execute: (
      phoneNumberNeeded: boolean,
      phoneValidationError?: proveAuth.PhoneValidationError
    ): Promise<PhoneNumberInput | null> => {
      return new Promise((resolve, reject) => {
        if (phoneNumberNeeded) {
          if (phoneValidationError) {
            reject(new Error(`Validation Error: ${phoneValidationError.message}`));
          }
          resolve({ phoneNumber: `+1${verifyYourIdentity.phone}` });
        } else {
          resolve(null);
        }
      });
    },
  };

  const authFinishStep: proveAuth.AuthFinishStepFn = async (): Promise<void> => {
    if (cancelledRef.current) return;
    const validateResponse = await validate({
      correlationId: correlationId as string,
    });
    if ('error' in validateResponse) {
      dispatch({
        type: CDX_ACTIONS.SUBMIT_API_ERROR,
        payload: validateResponse,
      });
    } else {
      if (validateResponse.success) {
        const { next, challengeMissing } = validateResponse;
        setNextStep(next);
        setChallengeMissing(challengeMissing);
      }
      next(currentStep, 'postSubmitOutcome', { validateResponse });
    }
  };


  const handleDidNotReceiveText = () => {
    cancelledRef.current = true;
    if (authRef.current) {
      authRef.current.cancel();
    }
 
    trackClick('CTA Clicked', {
      sub_screen_name: 'verify_mobile_support',
      cta_id: 'verification_resend',
      cta_text: 'I didn\'t receive a text',
      cta_type: 'link',
      cta_context: 'verification_challenge',
    });
     handleStep(Screens.IDENTITY_FAILED);
  };

  return (
    <>
      <div className="iLendingCDX-verify-mobile-number">
        <h1 className="iLendingCDX-form-title iLendingCDX-text-center">{t('verifyMobileNumber.checkYourPhone')}</h1>
        <p className="iLendingCDX-mb-8 iLendingCDX-text-center">{t('verifyMobileNumber.weNeedToVerifyYourMobileCarrier')}</p>
        <img
          src={AssetLinks.iphone}
          alt="phone icon"
        />
        <p className="iLendingCDX-mb-6 iLendingCDX-mt-4 iLendingCDX-text-center">
          {t('verifyMobileNumber.textMessageSent')}
          {getformattedPhoneNumber()}
        </p>
        <Button
          variant="ghost"
          size="md"
          className={'iLendingCDX-mb-4'}
          onClick={handleDidNotReceiveText}
        >
          {t('verifyMobileNumber.didNotReceiveText')}
        </Button>
      </div>
      <Footer text={t('verifyMobileNumber.footer')} />
    </>
  );
};

export default VerifyMobileNumberCheckYourPhone;
